import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * resources is an object that contains all the translations for the different languages.
 */
const resources = {
	en: {
		translation: {
			'Welcome to React': 'Welcome to React and react-i18next',
			// Global notification translations
			NOTIFICATIONS: 'Notifications',
			NOTIFICATIONS_NOT_SUPPORTED: 'Your browser does not support notifications',
			NOTIFICATIONS_NOT_SUPPORTED_TITLE: 'Notifications Not Supported',
			NOTIFICATIONS_NOT_SUPPORTED_MESSAGE: 'Your browser doesn\'t support push notifications. Please update your browser or use a different one.',
			CONTINUE_WITHOUT_NOTIFICATIONS: 'Continue Without Notifications',
			ENABLE_NOTIFICATIONS_TITLE: 'Enable Notifications - Required for Real-Time Updates',
			NOTIFICATIONS_REQUIRED_WARNING: 'Notifications are essential for this hotel management system!',
			NOTIFICATIONS_REQUIRED_DESCRIPTION: 'You need notifications enabled to receive real-time updates about:',
			ROOM_ASSIGNMENTS_UPDATES: 'Room assignments and changes',
			MAINTENANCE_UPDATES: 'Maintenance requests and updates',
			EXPRESS_CLEANING_UPDATES: 'Express cleaning requests',
			TEAM_COMMUNICATIONS: 'Team communications and alerts',
			NOTIFICATIONS_BLOCKED_TITLE: 'Notifications are currently blocked',
			NOTIFICATIONS_PERMISSION_INFO: 'Click "Allow Notifications" to enable real-time updates for your work.',
			SHOW_BROWSER_SETTINGS_HELP: 'Show Browser Settings Help',
			BROWSER_SETTINGS_HELP_TITLE: 'How to Enable Notifications',
			BROWSER_SETTINGS_HELP_MESSAGE: 'To enable notifications:\\n\\n1. Click the lock icon in your browser address bar\\n2. Change "Notifications" to "Allow"\\n3. Refresh the page\\n\\nOr go to your browser settings and allow notifications for this site.',
			IVE_ENABLED_NOTIFICATIONS: 'I\'ve Enabled Notifications',
			SKIP_NOT_RECOMMENDED: 'Skip (Not Recommended)',
			NOT_NOW: 'Not Now',
			DONT_ASK_AGAIN: 'Don\'t Ask Again',
			ALLOW_NOTIFICATIONS: 'Allow Notifications',
			REFRESH_PAGE_AFTER_ENABLING: 'Please enable notifications in your browser settings and refresh the page.',
			MARK_ALL_READ: 'Mark All Read',
			ENABLE_NOTIFICATIONS_MESSAGE: 'Enable notifications to receive updates about room assignments and maintenance requests',
			ENABLE_NOTIFICATIONS: 'Enable Notifications',
			NOTIFICATIONS_BLOCKED_MESSAGE: 'Push notifications have been blocked. Please enable them in your browser settings.',
			NO_NOTIFICATIONS: 'No notifications',
			SEEN: 'Seen',
			UNSEEN: 'Unseen',
			// Room assignment notification translations
			ROOM_ASSIGNED_TITLE: 'Room Assignment',
			ROOM_ASSIGNED_MESSAGE: 'You have been assigned rooms {{rooms}} for today',
			ROOM_REASSIGNED_TITLE: 'Room Reassignment',
			ROOM_REASSIGNED_FROM_MESSAGE: 'Rooms {{rooms}} have been reassigned to {{cleanerName}}',
			ROOM_REASSIGNED_TO_MESSAGE: 'You have been assigned rooms {{rooms}} (reassigned from {{previousCleaner}})',
			CLICK_TO_VIEW_ASSIGNMENTS: 'Click to view your room assignments',
			// PWA Installation translations
			PWA_INSTALL_TITLE: 'Install Fix & Clean',
			PWA_INSTALL_DESCRIPTION: 'Install Fix & Clean on your device for a better experience:',
			PWA_INSTALL_BENEFIT_HOME_SCREEN: 'Quick access from your home screen',
			PWA_INSTALL_BENEFIT_OFFLINE: 'Works offline with cached data',
			PWA_INSTALL_BENEFIT_NOTIFICATIONS: 'Receive push notifications',
			PWA_INSTALL_BENEFIT_FULLSCREEN: 'Full screen experience',
			PWA_INSTALL_BUTTON: 'Install App',
			PWA_INSTALL_NOT_NOW: 'Not Now',
			PWA_INSTALL_MAYBE_LATER: 'Maybe Later',
			PWA_INSTALL_IOS_TITLE: 'Install on iOS',
			PWA_INSTALL_IOS_DESCRIPTION: 'To install Fix & Clean on your iPhone or iPad:',
			PWA_INSTALL_IOS_STEP_1: 'Tap the Share button ⎘ at the bottom of Safari',
			PWA_INSTALL_IOS_STEP_2: 'Scroll down and tap "Add to Home Screen"',
			PWA_INSTALL_IOS_STEP_3: 'Tap "Add" in the top right corner',
			PWA_INSTALL_IOS_NOTE: 'This will create an app icon on your home screen for quick access.',
			PWA_INSTALL_IOS_GOT_IT: 'Got it',
			PWA_INSTALL_SUCCESS_MESSAGE: 'Fix & Clean has been installed successfully!',
			// User menu translations
			ACCOUNT: 'Account',
			LOG_OUT: 'Log out',
		},
		signUpPage: {
			// Sign up page translations
			SIGN_UP_TITLE: 'Create Your Hotel Account',
			STEP_PERSONAL_INFO: 'Personal Information',
			STEP_HOTEL_INFO: 'Hotel Information',
			FIRST_NAME_LABEL: 'First Name',
			FIRST_NAME_REQUIRED: 'First name is required',
			FIRST_NAME_MIN_LENGTH: 'First name must be at least 2 characters',
			LAST_NAME_LABEL: 'Last Name',
			LAST_NAME_REQUIRED: 'Last name is required',
			LAST_NAME_MIN_LENGTH: 'Last name must be at least 2 characters',
			USERNAME_LABEL: 'Username',
			USERNAME_REQUIRED: 'Username is required',
			USERNAME_MIN_LENGTH: 'Username must be at least 3 characters',
			USERNAME_INVALID: 'Username can only contain letters, numbers and underscores',
			EMAIL_LABEL: 'Email Address',
			EMAIL_REQUIRED: 'Email address is required',
			EMAIL_INVALID: 'Please enter a valid email address',
			PASSWORD_LABEL: 'Password',
			PASSWORD_REQUIRED: 'Password is required',
			PASSWORD_MIN_LENGTH: 'Password must be at least 8 characters',
			PASSWORD_TOO_WEAK: 'Password is too weak. Please use a stronger password',
			CONFIRM_PASSWORD_LABEL: 'Confirm Password',
			CONFIRM_PASSWORD_REQUIRED: 'Please confirm your password',
			PASSWORDS_DONT_MATCH: 'Passwords do not match',
			HOTEL_NAME_LABEL: 'Hotel Name',
			HOTEL_NAME_REQUIRED: 'Hotel name is required',
			HOTEL_NAME_MIN_LENGTH: 'Hotel name must be at least 2 characters',
			HOTEL_NAME_ALREADY_EXISTS: 'A hotel with this name already exists',
			HOTEL_DOMAIN_LABEL: 'Hotel Domain',
			HOTEL_DOMAIN_REQUIRED: 'Hotel domain is required',
			HOTEL_DOMAIN_INVALID: 'Please enter a valid domain',
			HOTEL_DOMAIN_ALREADY_EXISTS: 'This domain is already in use',
			BACK: 'Back',
			NEXT: 'Next',
			SIGN_UP_BUTTON: 'Create Account',
			SIGNING_UP: 'Creating Account...',
			HAVE_ACCOUNT: 'Already have an account?',
			SIGN_IN_LINK: 'Sign In',
			// Error messages
			EMAIL_ALREADY_EXISTS: 'An account with this email already exists',
			USERNAME_ALREADY_EXISTS: 'This username is already taken',
			NETWORK_ERROR: 'Network error. Please check your connection and try again',
			CONNECTION_TIMEOUT: 'Connection timeout. Please try again',
			SERVER_ERROR: 'Server error. Please try again later',
			SERVICE_UNAVAILABLE: 'Service temporarily unavailable. Please try again later',
			ACCOUNT_DISABLED: 'This account has been disabled',
			ACCOUNT_LOCKED: 'This account has been locked',
			BAD_REQUEST: 'Invalid request. Please check your information and try again',
			UNKNOWN_ERROR: 'An unexpected error occurred. Please try again',
		}
	},
	bg: {
		translation: {
			'Welcome to React': 'Добре дошли в React и react-i18next',
			// Global notification translations
			NOTIFICATIONS: 'Известия',
			NOTIFICATIONS_NOT_SUPPORTED: 'Вашият браузър не поддържа известия',
			NOTIFICATIONS_NOT_SUPPORTED_TITLE: 'Известията не се поддържат',
			NOTIFICATIONS_NOT_SUPPORTED_MESSAGE: 'Вашият браузър не поддържа push известия. Моля, обновете браузъра си или използвайте друг.',
			CONTINUE_WITHOUT_NOTIFICATIONS: 'Продължи без известия',
			ENABLE_NOTIFICATIONS_TITLE: 'Активиране на известия - Необходимо за актуализации в реално време',
			NOTIFICATIONS_REQUIRED_WARNING: 'Известията са от съществено значение за тази система за управление на хотели!',
			NOTIFICATIONS_REQUIRED_DESCRIPTION: 'Трябва да активирате известията, за да получавате актуализации в реално време за:',
			ROOM_ASSIGNMENTS_UPDATES: 'Назначения на стаи и промени',
			MAINTENANCE_UPDATES: 'Заявки за поддръжка и актуализации',
			EXPRESS_CLEANING_UPDATES: 'Заявки за експресно почистване',
			TEAM_COMMUNICATIONS: 'Комуникации и предупреждения на екипа',
			NOTIFICATIONS_BLOCKED_TITLE: 'Известията са блокирани',
			NOTIFICATIONS_PERMISSION_INFO: 'Натиснете "Разреши известия", за да активирате актуализации в реално време за вашата работа.',
			SHOW_BROWSER_SETTINGS_HELP: 'Покажи помощ за настройки на браузъра',
			BROWSER_SETTINGS_HELP_TITLE: 'Как да активирам известията',
			BROWSER_SETTINGS_HELP_MESSAGE: 'За да активирате известията:\\n\\n1. Натиснете иконката на катинара в адресната лента на браузъра\\n2. Променете "Известия" на "Разреши"\\n3. Опреснете страницата\\n\\nИли отидете в настройките на браузъра и разрешете известия за този сайт.',
			IVE_ENABLED_NOTIFICATIONS: 'Активирах известията',
			SKIP_NOT_RECOMMENDED: 'Пропусни (не се препоръчва)',
			NOT_NOW: 'Не сега',
			DONT_ASK_AGAIN: 'Не питай отново',
			ALLOW_NOTIFICATIONS: 'Разреши известия',
			REFRESH_PAGE_AFTER_ENABLING: 'Моля, активирайте известията в настройките на браузъра си и опреснете страницата.',
			MARK_ALL_READ: 'Маркирай всички като прочетени',
			ENABLE_NOTIFICATIONS_MESSAGE: 'Активирайте известията, за да получавате актуализации за назначения на стаи и заявки за поддръжка',
			ENABLE_NOTIFICATIONS: 'Активиране на известия',
			NOTIFICATIONS_BLOCKED_MESSAGE: 'Push известията са блокирани. Моля, активирайте ги в настройките на браузъра си.',
			NO_NOTIFICATIONS: 'Няма известия',
			SEEN: 'Видяно',
			UNSEEN: 'Не е видяно',
			// Room assignment notification translations
			ROOM_ASSIGNED_TITLE: 'Разпределение на стаи',
			ROOM_ASSIGNED_MESSAGE: 'Разпределени са ви стаи {{rooms}} за днес',
			ROOM_REASSIGNED_TITLE: 'Преразпределение на стаи',
			ROOM_REASSIGNED_FROM_MESSAGE: 'Стаи {{rooms}} са преразпределени на {{cleanerName}}',
			ROOM_REASSIGNED_TO_MESSAGE: 'Разпределени са ви стаи {{rooms}} (преразпределени от {{previousCleaner}})',
			CLICK_TO_VIEW_ASSIGNMENTS: 'Натиснете за преглед на разпределените стаи',
			// PWA Installation translations
			PWA_INSTALL_TITLE: 'Инсталиране на Fix & Clean',
			PWA_INSTALL_DESCRIPTION: 'Инсталирайте Fix & Clean на вашето устройство за по-добро изживяване:',
			PWA_INSTALL_BENEFIT_HOME_SCREEN: 'Бърз достъп от началния екран',
			PWA_INSTALL_BENEFIT_OFFLINE: 'Работи офлайн с кеширани данни',
			PWA_INSTALL_BENEFIT_NOTIFICATIONS: 'Получавайте push известия',
			PWA_INSTALL_BENEFIT_FULLSCREEN: 'Пълноекранно изживяване',
			PWA_INSTALL_BUTTON: 'Инсталирай приложението',
			PWA_INSTALL_NOT_NOW: 'Не сега',
			PWA_INSTALL_MAYBE_LATER: 'Може би по-късно',
			PWA_INSTALL_IOS_TITLE: 'Инсталиране на iOS',
			PWA_INSTALL_IOS_DESCRIPTION: 'За да инсталирате Fix & Clean на вашия iPhone или iPad:',
			PWA_INSTALL_IOS_STEP_1: 'Натиснете бутона за споделяне ⎘ в долната част на Safari',
			PWA_INSTALL_IOS_STEP_2: 'Превъртете надолу и натиснете "Добавяне към началния екран"',
			PWA_INSTALL_IOS_STEP_3: 'Натиснете "Добавяне" в горния десен ъгъл',
			PWA_INSTALL_IOS_NOTE: 'Това ще създаде икона на приложението на началния ви екран за бърз достъп.',
			PWA_INSTALL_IOS_GOT_IT: 'Разбрах',
			PWA_INSTALL_SUCCESS_MESSAGE: 'Fix & Clean беше инсталиран успешно!',
			// User menu translations
			ACCOUNT: 'Акаунт',
			LOG_OUT: 'Изход',
		},
		signUpPage: {
			// Sign up page translations
			SIGN_UP_TITLE: 'Създайте акаунта на вашия хотел',
			STEP_PERSONAL_INFO: 'Лична информация',
			STEP_HOTEL_INFO: 'Информация за хотела',
			FIRST_NAME_LABEL: 'Собствено име',
			FIRST_NAME_REQUIRED: 'Собственото име е задължително',
			FIRST_NAME_MIN_LENGTH: 'Собственото име трябва да бъде поне 2 символа',
			LAST_NAME_LABEL: 'Фамилно име',
			LAST_NAME_REQUIRED: 'Фамилното име е задължително',
			LAST_NAME_MIN_LENGTH: 'Фамилното име трябва да бъде поне 2 символа',
			USERNAME_LABEL: 'Потребителско име',
			USERNAME_REQUIRED: 'Потребителското име е задължително',
			USERNAME_MIN_LENGTH: 'Потребителското име трябва да бъде поне 3 символа',
			USERNAME_INVALID: 'Потребителското име може да съдържа само букви, цифри и долни черти',
			EMAIL_LABEL: 'Имейл адрес',
			EMAIL_REQUIRED: 'Имейл адресът е задължителен',
			EMAIL_INVALID: 'Моля, въведете валиден имейл адрес',
			PASSWORD_LABEL: 'Парола',
			PASSWORD_REQUIRED: 'Паролата е задължителна',
			PASSWORD_MIN_LENGTH: 'Паролата трябва да бъде поне 8 символа',
			PASSWORD_TOO_WEAK: 'Паролата е твърде слаба. Моля, използвайте по-силна парола',
			CONFIRM_PASSWORD_LABEL: 'Потвърдете паролата',
			CONFIRM_PASSWORD_REQUIRED: 'Моля, потвърдете паролата си',
			PASSWORDS_DONT_MATCH: 'Паролите не съвпадат',
			HOTEL_NAME_LABEL: 'Име на хотела',
			HOTEL_NAME_REQUIRED: 'Името на хотела е задължително',
			HOTEL_NAME_MIN_LENGTH: 'Името на хотела трябва да бъде поне 2 символа',
			HOTEL_NAME_ALREADY_EXISTS: 'Хотел с това име вече съществува',
			HOTEL_DOMAIN_LABEL: 'Домейн на хотела',
			HOTEL_DOMAIN_REQUIRED: 'Домейнът на хотела е задължителен',
			HOTEL_DOMAIN_INVALID: 'Моля, въведете валиден домейн',
			HOTEL_DOMAIN_ALREADY_EXISTS: 'Този домейн вече се използва',
			BACK: 'Назад',
			NEXT: 'Напред',
			SIGN_UP_BUTTON: 'Създай акаунт',
			SIGNING_UP: 'Създаване на акаунт...',
			HAVE_ACCOUNT: 'Вече имате акаунт?',
			SIGN_IN_LINK: 'Влезте',
			// Error messages
			EMAIL_ALREADY_EXISTS: 'Акаунт с този имейл вече съществува',
			USERNAME_ALREADY_EXISTS: 'Това потребителско име вече е заето',
			NETWORK_ERROR: 'Мрежова грешка. Моля, проверете връзката си и опитайте отново',
			CONNECTION_TIMEOUT: 'Изтекло време за връзка. Моля, опитайте отново',
			SERVER_ERROR: 'Сървърна грешка. Моля, опитайте отново по-късно',
			SERVICE_UNAVAILABLE: 'Услугата временно не е налична. Моля, опитайте отново по-късно',
			ACCOUNT_DISABLED: 'Този акаунт е деактивиран',
			ACCOUNT_LOCKED: 'Този акаунт е заключен',
			BAD_REQUEST: 'Невалидна заявка. Моля, проверете информацията си и опитайте отново',
			UNKNOWN_ERROR: 'Възникна неочаквана грешка. Моля, опитайте отново',
		}
	}
};

/**
 * i18n is initialized with the resources object and the language to use.
 * The keySeparator option is set to false because we do not use keys in form messages.welcome.
 * The interpolation option is set to false because we do not use interpolation in form messages.welcome.
 */

//here we can use the propel property for language and pass it when initializing

i18n.use(initReactI18next) // passes i18n down to react-i18next
	.init({
		resources,
		lng: 'bg',

		keySeparator: false, // we do not use keys in form messages.welcome

		interpolation: {
			escapeValue: false // react already safes from xss
		}
	});

export default i18n;
